import { AdminLayout } from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Search,
  Loader2,
  User,
  Edit2,
  Trash2,
  MapPin,
  Phone,
  Mail,
} from "lucide-react";
import { useState, useEffect, useCallback, useRef } from "react";
import { adminService, RegisteredUser } from "../../services/adminService";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";

const UserRegistration = () => {
  const [users, setUsers] = useState<RegisteredUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [pagination, setPagination] = useState({
    count: 0,
    page: 1,
    pageSize: 10,
  });

  // Edit Modal State
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<RegisteredUser | null>(null);
  const [editFormData, setEditFormData] = useState<Partial<RegisteredUser>>({});
  const [isUpdating, setIsUpdating] = useState(false);

  const fetchUsers = useCallback(
    async (page = 1, search = "") => {
      try {
        setLoading(true);
        const data = await adminService.getRegisteredUsers({
          page: page,
          page_size: pagination.pageSize,
          search: search,
          ordering: "id", // Order by id ASC as requested
        });
        setUsers(data?.results ?? []);
        setPagination(prev => ({
          ...prev,
          count: data?.count ?? 0,
          page: page,
        }));
      } catch (error: any) {
        console.error("Error fetching users:", error);
        toast.error(
          error?.response?.data?.detail ??
            "Failed to load user registrations. Please try again."
        );
      } finally {
        setLoading(false);
      }
    },
    [pagination.pageSize]
  );

  const handleDelete = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this user profile?")) {
      return;
    }

    try {
      await adminService.deleteRegisteredUser(id);
      toast.success("User profile deleted successfully");
      fetchUsers(pagination.page, searchTerm);
    } catch (error: any) {
      console.error("Error deleting user:", error);
      toast.error("Failed to delete user profile");
    }
  };

  const handleEditClick = (user: RegisteredUser) => {
    setSelectedUser(user);
    setEditFormData({
      first_name: user.first_name ?? "",
      last_name: user.last_name ?? "",
      phone: user.phone ?? user.phone_number ?? "",
      city: user.city ?? "",
      state: user.state ?? "",
      country: user.country ?? "",
      address: user.address ?? "",
      postal_code: user.postal_code ?? "",
      is_active: user.is_active !== false,
    });
    setIsEditModalOpen(true);
  };

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) {
      return;
    }

    try {
      setIsUpdating(true);
      await adminService.updateRegisteredUser(selectedUser.id, editFormData);
      toast.success("User profile updated successfully");
      setIsEditModalOpen(false);
      fetchUsers(pagination.page, searchTerm);
    } catch (error: any) {
      console.error("Error updating user:", error);
      toast.error(
        error?.response?.data?.phone?.[0] ?? "Failed to update user profile"
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const fetchedRef = useRef(false);
  useEffect(() => {
    if (fetchedRef.current) {
      return;
    }
    fetchedRef.current = true;
    fetchUsers();
    return () => {
      fetchedRef.current = false;
    };
  }, [fetchUsers]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchUsers(1, searchTerm);
  };

  const totalPages = Math.ceil(pagination.count / pagination.pageSize);

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-3xl font-bold text-foreground">
              User Registration
            </h2>
            <p className="text-sm text-muted-foreground">
              Manage and view all registered user profiles.
            </p>
          </div>
          <form
            onSubmit={handleSearch}
            className="flex w-full max-w-sm items-center gap-2"
          >
            <div className="relative w-full">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search users..."
                className="w-full pl-9"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
            <Button type="submit" variant="secondary">
              Search
            </Button>
          </form>
        </div>

        <div className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-muted/50">
                <tr>
                  <th className="w-16 px-4 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    ID
                  </th>
                  <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    Email
                  </th>
                  <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    Phone
                  </th>
                  <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    Location
                  </th>
                  <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    Status
                  </th>
                  <th className="px-4 py-3 text-right font-semibold uppercase tracking-wider text-muted-foreground">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {loading && users?.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center">
                      <div className="flex flex-col items-center gap-2 text-muted-foreground">
                        <Loader2 className="h-8 w-8 animate-spin" />
                        <p>Loading users...</p>
                      </div>
                    </td>
                  </tr>
                ) : users?.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-4 py-12 text-center text-muted-foreground"
                    >
                      No registered users found.
                    </td>
                  </tr>
                ) : (
                  users?.map(user => (
                    <tr
                      key={user.id}
                      className="transition-colors hover:bg-muted/30"
                    >
                      <td className="px-4 py-3 font-medium">{user.id}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span>
                            {(user.full_name ??
                              `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim()) ||
                              "N/A"}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">{user.email}</td>
                      <td className="px-4 py-3">{user.phone_number ?? "-"}</td>
                      <td className="px-4 py-3">
                        {[user.city, user.state, user.country]
                          .filter(Boolean)
                          .join(", ") || "-"}
                      </td>
                      <td className="px-4 py-3 capitalize">
                        {user.user_type ?? "User"}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                            user.is_active !== false
                              ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                              : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                          }`}
                        >
                          {user.is_active !== false ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-primary hover:bg-primary/50"
                            onClick={() => handleEditClick(user)}
                            title="Edit User Details"
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:bg-destructive/80"
                            onClick={() => handleDelete(user.id)}
                            title="Delete User"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination Controls */}
        <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
          <div className="text-sm text-muted-foreground">
            {pagination.count > 0 ? (
              <>
                Showing{" "}
                <span className="font-medium text-foreground">
                  {(pagination.page - 1) * pagination.pageSize + 1}
                </span>{" "}
                to{" "}
                <span className="font-medium text-foreground">
                  {Math.min(
                    pagination.page * pagination.pageSize,
                    pagination.count
                  )}
                </span>{" "}
                of{" "}
                <span className="font-medium text-foreground">
                  {pagination.count}
                </span>{" "}
                results
              </>
            ) : (
              "No results to show"
            )}
          </div>
          <div className="flex flex-wrap justify-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchUsers(1, searchTerm)}
              disabled={pagination.page === 1 || loading}
            >
              « First
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchUsers(pagination.page - 1, searchTerm)}
              disabled={pagination.page === 1 || loading}
            >
              ‹ Back
            </Button>
            <div className="flex h-9 items-center rounded-md border bg-muted/50 px-4 text-sm font-medium">
              Page {pagination.page} of {totalPages || 1}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchUsers(pagination.page + 1, searchTerm)}
              disabled={pagination.page >= totalPages || loading}
            >
              Next ›
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchUsers(totalPages, searchTerm)}
              disabled={pagination.page >= totalPages || loading}
            >
              Last »
            </Button>
          </div>
        </div>
      </div>

      {/* Edit User Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="max-w-2xl overflow-hidden border-none p-0 shadow-2xl">
          <DialogHeader className="border-b bg-muted/30 px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                <User className="h-5 w-5" />
              </div>
              <div>
                <DialogTitle className="text-xl font-bold">
                  Edit User Details
                </DialogTitle>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Update profile information for {selectedUser?.email}
                </p>
              </div>
            </div>
          </DialogHeader>

          <form onSubmit={handleUpdateUser}>
            <ScrollArea className="max-h-[70vh] px-6 py-6">
              <div className="space-y-6">
                {/* Basic Info Section */}
                <div>
                  <h4 className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Basic Information
                  </h4>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="first_name" required>
                        First Name
                      </Label>
                      <Input
                        id="first_name"
                        value={editFormData.first_name}
                        onChange={e =>
                          setEditFormData({
                            ...editFormData,
                            first_name: e.target.value,
                          })
                        }
                        placeholder="John"
                        className="border-muted-foreground/20 bg-muted/30 transition-colors focus:bg-background"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="last_name" required>
                        Last Name
                      </Label>
                      <Input
                        id="last_name"
                        value={editFormData.last_name}
                        onChange={e =>
                          setEditFormData({
                            ...editFormData,
                            last_name: e.target.value,
                          })
                        }
                        placeholder="Doe"
                        className="border-muted-foreground/20 bg-muted/30 transition-colors focus:bg-background"
                      />
                    </div>
                  </div>
                </div>

                {/* Contact Info Section */}
                <div>
                  <h4 className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Contact & Status
                  </h4>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="phone"
                          value={editFormData.phone}
                          onChange={e =>
                            setEditFormData({
                              ...editFormData,
                              phone: e.target.value,
                            })
                          }
                          placeholder="10 digit number"
                          className="border-muted-foreground/20 bg-muted/30 pl-10 transition-colors focus:bg-background"
                          maxLength={10}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Account Status</Label>
                      <div className="flex items-center space-x-3 rounded-md border border-muted-foreground/10 bg-muted/30 p-2">
                        <Switch
                          id="is_active"
                          checked={editFormData.is_active}
                          onCheckedChange={checked =>
                            setEditFormData({
                              ...editFormData,
                              is_active: checked,
                            })
                          }
                        />
                        <Label
                          htmlFor="is_active"
                          className="cursor-pointer font-normal"
                        >
                          User account is{" "}
                          {editFormData.is_active ? "Active" : "Inactive"}
                        </Label>
                        <Badge
                          variant={
                            editFormData.is_active ? "default" : "secondary"
                          }
                          className="pointer-events-none ml-auto"
                        >
                          {editFormData.is_active ? "Active" : "Disabled"}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Location Section */}
                <div>
                  <h4 className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Location Details
                  </h4>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="address">Address</Label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="address"
                          value={editFormData.address}
                          onChange={e =>
                            setEditFormData({
                              ...editFormData,
                              address: e.target.value,
                            })
                          }
                          placeholder="Street address"
                          className="border-muted-foreground/20 bg-muted/30 pl-10 transition-colors focus:bg-background"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                      <div className="space-y-2">
                        <Label htmlFor="city">City</Label>
                        <Input
                          id="city"
                          value={editFormData.city}
                          onChange={e =>
                            setEditFormData({
                              ...editFormData,
                              city: e.target.value,
                            })
                          }
                          placeholder="City"
                          className="border-muted-foreground/20 bg-muted/30 transition-colors focus:bg-background"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="state">State</Label>
                        <Input
                          id="state"
                          value={editFormData.state}
                          onChange={e =>
                            setEditFormData({
                              ...editFormData,
                              state: e.target.value,
                            })
                          }
                          placeholder="State"
                          className="border-muted-foreground/20 bg-muted/30 transition-colors focus:bg-background"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="country">Country</Label>
                        <Input
                          id="country"
                          value={editFormData.country}
                          onChange={e =>
                            setEditFormData({
                              ...editFormData,
                              country: e.target.value,
                            })
                          }
                          placeholder="Country"
                          className="border-muted-foreground/20 bg-muted/30 transition-colors focus:bg-background"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollArea>

            <DialogFooter className="items-center border-t bg-muted/30 px-6 py-4">
              <span className="mr-auto flex items-center gap-1 text-xs text-muted-foreground">
                <Mail className="h-3 w-3" /> {selectedUser?.email}
              </span>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsEditModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isUpdating}
                className="min-w-[100px]"
              >
                {isUpdating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save Changes"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default UserRegistration;
